import { cloneDeep, isEqual, pick, remove } from "lodash";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { createClusterLearningObjective } from "./clusterLearningObjectiveModel";
import { clusterManager } from "./clusterManager";
import {
  CLUSTER_HEIGHT,
  COLUMN_WIDTH,
  DEFAULT_CLUSTER_OBJECT,
} from "./learningObjectivesConstants";
import { Planable } from "./planableModel";

// Fields the cluster modal binds to directly. Snapshotted on open so closing
// without saving restores them. The planning period is deliberately absent:
// the modal keeps it in local refs and only applies it on save.
const EDITABLE_CLUSTER_FIELDS = [
  "name",
  "description",
  "color",
  "editable",
  "attachments",
  "links",
  "videos",
];

// When we have the time to refactor this, we should consider using the same approach as in the clusterManager use es6 modules instead of Classes
export class Cluster extends Planable {
  id;
  school_id;
  name;
  description;
  color;
  type;
  user;
  shortage;
  subjects;
  education_year_levels;
  editable;
  lesson_class;
  library_learning_objectives;
  temporary;
  add_to_library;
  library_cluster_id;
  videos;
  links;
  attachments;
  initialValue = {};
  library_learning_objective_ids;
  meta = {};

  constructor(data = {}) {
    super(data);

    this.id = data.id || this.generateTempId();
    this.name = data.name || DEFAULT_CLUSTER_OBJECT.name;
    this.color = data.color || DEFAULT_CLUSTER_OBJECT.color;
    this.type = data.type || DEFAULT_CLUSTER_OBJECT.type;
    this.school_id = data.school_id || DEFAULT_CLUSTER_OBJECT.school_id;
    this.description = data.description || DEFAULT_CLUSTER_OBJECT.description;
    this.user = data.user || DEFAULT_CLUSTER_OBJECT.user;
    this.shortage = data.shortage || DEFAULT_CLUSTER_OBJECT.shortage;
    this.subjects = data.subjects || DEFAULT_CLUSTER_OBJECT.subjects;
    this.education_year_levels =
      data.education_year_levels ||
      DEFAULT_CLUSTER_OBJECT.education_year_levels;
    this.editable = data.editable ?? DEFAULT_CLUSTER_OBJECT.editable;
    this.library_learning_objectives =
      data.library_learning_objectives ||
      DEFAULT_CLUSTER_OBJECT.library_learning_objectives;
    this.lesson_class =
      data.lesson_class || DEFAULT_CLUSTER_OBJECT.lesson_class;
    this.temporary = data.temporary ?? DEFAULT_CLUSTER_OBJECT.temporary;
    this.add_to_library =
      data.add_to_library ?? DEFAULT_CLUSTER_OBJECT.add_to_library;
    this.library_cluster_id =
      data.library_cluster_id || DEFAULT_CLUSTER_OBJECT.library_cluster_id;
    this.subject_name =
      data.subject_name || DEFAULT_CLUSTER_OBJECT.subject_name;
    this.subject_shortage =
      data.subject_shortage || DEFAULT_CLUSTER_OBJECT.subject_shortage;
    this.meta.can_update =
      data.meta?.can_update !== undefined
        ? data.meta.can_update
        : DEFAULT_CLUSTER_OBJECT.meta.can_update;
    this.meta.can_delete =
      data.meta?.can_delete !== undefined
        ? data.meta.can_delete
        : DEFAULT_CLUSTER_OBJECT.meta.can_delete;
    this.videos = data.videos || DEFAULT_CLUSTER_OBJECT.videos;
    this.attachments = data.attachments || DEFAULT_CLUSTER_OBJECT.attachments;
    this.library_learning_objective_ids =
      data.library_learning_objective_ids ||
      DEFAULT_CLUSTER_OBJECT.library_learning_objective_ids;
    this.links = data.links || DEFAULT_CLUSTER_OBJECT.links;

    this.setClusterLearningObjectives(data.learning_objectives);

    // planning meta data
    this.planningMeta.isDraggable = true;
    this.planningMeta.canDrag = true;
    this.planningMeta.manager = clusterManager.value;
    this.planningMeta.groupId = this.lesson_class;

    this.updateInitialValue();
  }

  setClusterLearningObjectives(learningObjectives) {
    this.learning_objectives = cloneDeep(
      DEFAULT_CLUSTER_OBJECT.learning_objectives,
    );

    if (
      !learningObjectives ||
      !Array.isArray(learningObjectives) ||
      !learningObjectives.length
    ) {
      return;
    }

    // This will create and add all the learning objectives to the cluster
    learningObjectives.forEach((learningObjective) => {
      this.learning_objectives.push(
        createClusterLearningObjective({
          ...learningObjective,
          cluster_id: this.id,
        }),
      );
    });
  }

  async addLearningObjectivesToModel(addedLearningObjectives) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { postLearningObjectivesToModel } = learningObjectivesStore;

    if (this.id && !this.temporary) {
      // Existing cluster, so post the learning objectives to the model
      await postLearningObjectivesToModel(addedLearningObjectives, this);
    } else {
      // New cluster, so we need to add the learning objectives to the cluster
      this.learning_objectives.push(
        ...addedLearningObjectives.filter(
          (learningObjective) =>
            !this.learning_objectives.some(
              (existingLearningObjective) =>
                existingLearningObjective.id === learningObjective.id,
            ),
        ),
      );
    }
  }

  async addClusterLearningObjective(learningObjective) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { addLearningObjective } = learningObjectivesStore;

    const { data } = await addLearningObjective({
      learningObjective,
      clusterId: this.id,
    });

    this.learning_objectives.push(createClusterLearningObjective(data));
  }

  async removeLearningObjectiveFromCluster(learningObjective) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { deleteLearningObjective } = learningObjectivesStore;

    try {
      // Remove the learning objective from the cluster
      await deleteLearningObjective(learningObjective);
      remove(this.learning_objectives, ({ id }) => id === learningObjective.id);
    } catch (e) {
      throw new Error(e);
    }
  }

  // --- Modal editing (staged, persisted on save) -----------------------------
  // The modal binds straight to the cluster so the bar updates live while you
  // type/pick a colour. That means closing without saving has to put the
  // fields back — snapshot them on open.
  beginEdit() {
    this.editSnapshot = cloneDeep(pick(this, EDITABLE_CLUSTER_FIELDS));
    this.beginLearningObjectiveEdit();
  }

  revertEdit() {
    if (this.editSnapshot) {
      Object.assign(this, cloneDeep(this.editSnapshot));
      this.editSnapshot = null;
    }

    this.revertLearningObjectiveEdit();
  }

  // Saved: the current values are the new baseline, nothing left to revert.
  commitEdit() {
    this.editSnapshot = null;
  }

  hasEditChanges() {
    if (!this.editSnapshot) {
      return false;
    }

    if (!isEqual(this.editSnapshot, pick(this, EDITABLE_CLUSTER_FIELDS))) {
      return true;
    }

    return !isEqual(this.learningObjectiveSnapshot, this.learning_objectives);
  }

  // The modal edits learning_objectives in place; snapshot so a save can diff
  // and a cancel can restore.
  beginLearningObjectiveEdit() {
    this.learningObjectiveSnapshot = cloneDeep(this.learning_objectives);
  }

  revertLearningObjectiveEdit() {
    if (!this.learningObjectiveSnapshot) {
      return;
    }

    this.learning_objectives = cloneDeep(this.learningObjectiveSnapshot);
    this.learningObjectiveSnapshot = null;
  }

  // Sync the staged changes to the backend: detach removed, attach added, then
  // set each objective's order. Matched by library id since staged additions
  // are raw library objectives (no cluster-objective id yet).
  async persistLearningObjectives() {
    const learningObjectivesStore = useLearningObjectivesStore();
    const {
      deleteLearningObjective,
      postLearningObjectivesToModel,
      updateLearningObjectiveOrder,
    } = learningObjectivesStore;

    const snapshot = this.learningObjectiveSnapshot ?? [];
    this.learningObjectiveSnapshot = null;

    const libraryId = (learningObjective) =>
      learningObjective.library_learning_objective_id ?? learningObjective.id;

    const desiredIds = this.learning_objectives.map(libraryId);
    const snapshotIds = snapshot.map(libraryId);

    // Detach removed.
    for (const learningObjective of snapshot) {
      if (!desiredIds.includes(libraryId(learningObjective))) {
        await deleteLearningObjective(learningObjective);
      }
    }

    // Attach added (the endpoint only attaches ids not already present, and
    // replaces this.learning_objectives with the persisted objectives).
    const added = this.learning_objectives.filter(
      (learningObjective) =>
        !snapshotIds.includes(libraryId(learningObjective)),
    );

    if (added.length) {
      await postLearningObjectivesToModel(added, this);
    }

    // Persist the order (one "move" per objective whose position changed).
    // Diff against the snapshot's positions — the reorder handler already
    // rewrote each objective's local `order` to its index, so that can't tell
    // us what moved.
    const originalIndex = new Map(
      snapshot.map((learningObjective, index) => [
        libraryId(learningObjective),
        index,
      ]),
    );

    for (let index = 0; index < desiredIds.length; index++) {
      const learningObjective = this.learning_objectives.find(
        (item) => libraryId(item) === desiredIds[index],
      );

      if (!learningObjective) {
        continue;
      }

      // Moved (position differs) or newly added (not in the snapshot).
      if (originalIndex.get(desiredIds[index]) !== index) {
        await updateLearningObjectiveOrder({
          learningObjectiveId: learningObjective.id,
          clusterId: this.id,
          order: index,
        });

        learningObjective.order = index;
      }
    }

    this.learning_objectives.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getAdditionalData() {
    const { lesson_class, subject_shortage } = this;

    return {
      lesson_class,
      subject_shortage,
    };
  }

  /**
   * This function returns the values that we want to check for changes
   * @returns {any}
   */
  valuesToCheckForChanges() {
    // Only the cluster's data and its grid placement (rowIndex + weekIndexes)
    // count as changes. The rest of planningMeta is transient interaction state
    // (cursor positions, handler refs, resize/move flags) that mutates during a
    // drag even when the cluster ends where it started — including it would make
    // every release look like a change.
    const {
      planningMeta: { rowIndex, weekIndexes },
      initialValue, // eslint-disable-line no-unused-vars
      editSnapshot, // eslint-disable-line no-unused-vars
      learningObjectiveSnapshot, // eslint-disable-line no-unused-vars
      user, // eslint-disable-line no-unused-vars
      subjects, // eslint-disable-line no-unused-vars
      learning_objectives, // eslint-disable-line no-unused-vars
      library_learning_objectives, // eslint-disable-line no-unused-vars
      library_cluster_id, // eslint-disable-line no-unused-vars
      education_year_levels, // eslint-disable-line no-unused-vars
      ...clusterValuesToCheck
    } = this;

    return Object.assign({}, clusterValuesToCheck, {
      planningMeta: { rowIndex, weekIndexes },
    });
  }

  /**
   * This function updates the initial value without the reference
   * @returns {any}
   */
  updateInitialValue() {
    this.initialValue = cloneDeep(this.valuesToCheckForChanges());
  }

  updateStartWeekByIndex(
    newStartIndex,
    currentStartWeekIndex,
    removeWeekFromRow,
    addWeekToRow,
  ) {
    const diff = Math.abs(newStartIndex - currentStartWeekIndex);
    const isExpanding = newStartIndex > currentStartWeekIndex;

    for (let i = 1; i <= diff; i++) {
      if (isExpanding) {
        // First set the new start week and year
        this.setStartWeekAndYear(currentStartWeekIndex + i);

        // Add a new column to the row before this cluster to fill the gap
        addWeekToRow(
          this,
          this.planningMeta.rowIndex,
          this.planningMeta.weekIndexes[0],
        );

        continue;
      }

      // First remove the week from the curent row and after we update the end week and year
      removeWeekFromRow(
        this.planningMeta.rowIndex,
        currentStartWeekIndex + -i,
        this.planningMeta.groupIndex,
      );

      // Update the start week and year
      this.setStartWeekAndYear(currentStartWeekIndex + -i);
    }
  }

  updateEndWeekByIndex(
    newEndIndex,
    currentEndWeekIndex,
    removeWeekFromRow,
    addWeekToRow,
  ) {
    const diff = Math.abs(newEndIndex - currentEndWeekIndex);
    const isExpanding = newEndIndex > currentEndWeekIndex;

    // First remove the week from the curent row and after we update the end week and year
    for (let i = 1; i <= diff; i++) {
      if (isExpanding) {
        // Remove the first row after this cluster
        removeWeekFromRow(
          this.planningMeta.rowIndex,
          currentEndWeekIndex + i,
          this.planningMeta.groupIndex,
        );

        // Update the end week and year
        this.setEndWeekAndYear(currentEndWeekIndex + i);

        continue;
      }

      // Add a new column to the row after this cluster
      this.setEndWeekAndYear(currentEndWeekIndex + -i);

      addWeekToRow(
        this,
        this.planningMeta.rowIndex,
        this.planningMeta.weekIndexes[this.planningMeta.weekIndexes.length - 1],
        true,
      );
    }
  }

  // --- Live grid-snapped move -------------------------------------------------
  // Dragging the bar body slides the whole cluster along the timeline, snapping
  // to week columns (like resizing an edge) instead of floating and dropping.
  // Overrides Planable.setDraggable (which uses drop-based DnD, still used by
  // periods) so the move tracks the grid live and preserves the grab offset.
  setDraggable(element, handle) {
    if (!this.isDraggable() || !handle) {
      return;
    }

    this.planningMeta.onMoveDown = (event) => this.startMove(event);
    handle.addEventListener("mousedown", this.planningMeta.onMoveDown);
  }

  startMove(event) {
    // Left button only, and never while a resize handle is active.
    if (event.button !== 0 || this.planningMeta.isResizing) {
      return;
    }

    // Stop the bar's text from being selected while dragging.
    event.preventDefault();

    this.planningMeta.isMoving = true;
    this.planningMeta.moveStartX = event.clientX;
    this.planningMeta.moveStartY = event.clientY;
    this.planningMeta.lastMoveX = event.clientX;
    this.planningMeta.lastMoveY = event.clientY;
    this.planningMeta.moveFrame = null;

    // Snapshots the resting layout so other bars can reflow and spring back.
    this.getManager().beginMove(this);
    this.getManager().setDraggingStateOnClusterGroup(this.lesson_class, true);

    this.planningMeta.onMoveMove = (moveEvent) => this.queueMove(moveEvent);
    this.planningMeta.onMoveUp = () => this.stopMove();

    document.addEventListener("mousemove", this.planningMeta.onMoveMove);
    document.addEventListener("mouseup", this.planningMeta.onMoveUp);
  }

  // Coalesce mousemoves to one grid update per animation frame — snappy and
  // jank-free (moveDelta itself no-ops until the bar actually crosses a cell).
  queueMove(event) {
    this.planningMeta.lastMoveX = event.clientX;
    this.planningMeta.lastMoveY = event.clientY;

    if (this.planningMeta.moveFrame !== null) {
      return;
    }

    this.planningMeta.moveFrame = requestAnimationFrame(() => {
      this.planningMeta.moveFrame = null;
      this.applyMove();
    });
  }

  applyMove() {
    if (!this.planningMeta.isMoving) {
      return;
    }

    const changeInColumns = Math.round(
      (this.planningMeta.lastMoveX - this.planningMeta.moveStartX) /
        COLUMN_WIDTH,
    );
    const changeInRows = Math.round(
      (this.planningMeta.lastMoveY - this.planningMeta.moveStartY) /
        CLUSTER_HEIGHT,
    );

    this.getManager().moveDelta(this, changeInColumns, changeInRows);
  }

  stopMove() {
    if (this.planningMeta.moveFrame !== null) {
      cancelAnimationFrame(this.planningMeta.moveFrame);
      this.planningMeta.moveFrame = null;
    }

    // A mouseup can beat the queued animation frame; apply the last pointer
    // position now so the final cell crossing isn't dropped (which would save
    // the cluster one cell behind where the user let go).
    this.applyMove();

    document.removeEventListener("mousemove", this.planningMeta.onMoveMove);
    document.removeEventListener("mouseup", this.planningMeta.onMoveUp);

    this.getManager().setDraggingStateOnClusterGroup(this.lesson_class, false);
    this.getManager().endMove();

    this.planningMeta.isMoving = false;

    // Store the new placement (and any bars it bumped) right away.
    this.getManager().persistOnRelease();
  }

  // Persist a resize (start/end change) as soon as the handle is released.
  stopResize() {
    super.stopResize();

    this.getManager().persistOnRelease();
  }

  // Reposition the whole span so it starts at newStartIndex, keeping duration.
  moveToStartIndex(newStartIndex) {
    const { week: startWeek, year: startYear } =
      this.getWeekByIndex(newStartIndex);
    const { week: endWeek, year: endYear } = this.getWeekByIndex(
      newStartIndex + this.duration - 1,
    );

    this.start_week = startWeek;
    this.start_year = startYear;
    this.end_week = endWeek;
    this.end_year = endYear;

    this.generateWeekIndexes();
  }

  setEndWeekAndYear(index) {
    const { week, year } = this.getWeekByIndex(index);

    this.end_week = week;
    this.end_year = year;

    this.generateWeekIndexes();
  }

  setStartWeekAndYear(index) {
    const { week, year } = this.getWeekByIndex(index);

    this.start_week = week;
    this.start_year = year;

    this.generateWeekIndexes();
  }

  getStartWeekIndex() {
    return this.getWeekIndex(this.start_week, this.start_year);
  }

  getEndWeekIndex() {
    return this.getWeekIndex(this.end_week, this.end_year);
  }

  async delete() {
    if (this.temporary) {
      return Promise.resolve();
    }

    const learningObjectivesStore = useLearningObjectivesStore();
    const { deleteCluster } = learningObjectivesStore;

    await deleteCluster(this.id);
  }

  async update({ onUploadProgress } = {}) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { updateCluster } = learningObjectivesStore;

    // Hide meta en weekIndexes from the update request, plus the local
    // edit snapshots — they're client-side undo state, not API fields.
    const {
      planningMeta, // eslint-disable-line no-unused-vars
      initialValue, // eslint-disable-line no-unused-vars
      editSnapshot, // eslint-disable-line no-unused-vars
      learningObjectiveSnapshot, // eslint-disable-line no-unused-vars
      ...rest
    } = this;

    try {
      const { data } = await updateCluster(
        {
          ...rest,
          row_index: this.planningMeta.rowIndex,
        },
        { onUploadProgress },
      );

      // eslint-disable-next-line no-unused-vars
      const { learning_objectives, ...clusterData } = data;

      Object.assign(this, { ...clusterData, temporary: false });

      this.updateInitialValue();
    } catch (error) {
      console.log("🚀 ~ Cluster ~ update ~ error:", error);
      return Promise.reject(error);
    }
  }

  async store({ onUploadProgress } = {}) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { storeClusterFromLibrary, storeCluster } = learningObjectivesStore;

    // Hide meta en weekIndexes from the update request, plus the local
    // edit snapshots — they're client-side undo state, not API fields.
    const {
      planningMeta, // eslint-disable-line no-unused-vars
      initialValue, // eslint-disable-line no-unused-vars
      editSnapshot, // eslint-disable-line no-unused-vars
      learningObjectiveSnapshot, // eslint-disable-line no-unused-vars
      ...rest
    } = this;

    try {
      const { data } =
        this.library_cluster_id == null
          ? await storeCluster(
              {
                ...rest,
                row_index: this.planningMeta.rowIndex,
                // Attach the staged leerdoelen in the same create request so the
                // response already carries them — no save-then-add second call.
                library_learning_objective_ids: this.learning_objectives.map(
                  (learningObjective) =>
                    learningObjective.library_learning_objective_id ??
                    learningObjective.id,
                ),
              },
              { onUploadProgress },
            )
          : await storeClusterFromLibrary(
              { ...rest, row_index: this.planningMeta.rowIndex },
              { onUploadProgress },
            );

      const { learning_objectives, ...clusterData } = data;

      Object.assign(this, { ...clusterData, temporary: false });

      this.setClusterLearningObjectives(learning_objectives);

      this.updateInitialValue();
    } catch (error) {
      console.log("🚀 ~ Cluster ~ upsert ~ error:", error, this);
      return Promise.reject(error);
    }
  }

  hasChanges() {
    return !isEqual(this.initialValue, this.valuesToCheckForChanges());
  }

  /**
   * This function updates the cluster that have been changed
   * @returns {any}
   */
  async upsert({ onUploadProgress } = {}) {
    // A temporary cluster must always be stored, even without field edits — its
    // placement alone is the change. Existing clusters only persist when changed.
    if (!this.temporary && !this.hasChanges()) {
      return;
    }

    return this.temporary
      ? await this.store({ onUploadProgress })
      : await this.update({ onUploadProgress });
  }
}
